import type { SystemContext } from '@chakra-ui/react'
import React from 'react'
import mangoLogo from '~/assets/mango-logo-gradient.png'
import mangoLogoWhite from '~/assets/MangoLogo.svg'
import dentallyLogoWhite from '~/assets/partners/Dentally-logo-white.png'
import dentallyLogo from '~/assets/partners/Dentally-logo.png'
import denticonLogoWhite from '~/assets/partners/Denticon-logo-white.png'
import denticonLogo from '~/assets/partners/Denticon-logo.png'
import dentrixAscendLogoWhite from '~/assets/partners/Dentrix-Ascend-logo-white.svg'
import dentrixAscendLogo from '~/assets/partners/Dentrix-Ascend-logo.png'
import dentrixLogoWhite from '~/assets/partners/Dentrix-logo-white.svg'
import dentrixLogo from '~/assets/partners/Dentrix-logo.png'
import maxAssistIcon from '~/assets/partners/MaxAssist-icon.svg'
import { useUserState } from '~/atoms/UserState'
import urls from '~/constants/urls'
import { DesktopAppPlatform, getDesktopAppPlatform } from '~/utils/deviceType'
import {
  canLookupInstalledAppPartner,
  fetchInstalledAppPartner,
  Partners,
  resolvePartnerId,
} from '~/utils/partner'
import type { PartnerId } from '~/utils/partner'

import {
  system as defaultTheme,
  dentallyTheme,
  denticonTheme,
  dentrixTheme,
  maxAssistTheme,
} from '@mangovoice/design-system'

export { Partners }
export type { PartnerId }

export type DesktopAppInstallers = Record<DesktopAppPlatform, string>

/**
 * Partner configuration with associated properties
 */
export interface PartnerConfig {
  partnerKey?: PartnerId
  logo: string
  popupLogo: string
  theme: SystemContext
  // Desktop product name, mirrors electron partner-configs `productName`. Only
  // set for partners that ship a desktop build; others fall back to Mango Voice.
  desktopAppName?: string
  // Branded installers; partners without one download the Mango Voice app.
  desktopAppInstallers?: DesktopAppInstallers
  // Dynamic fields from pbx.partner
  deepLinkPattern?: string
  // If the partner uses a different field for the external ID, specify it here
  externalIdFieldByGrove?: {
    grove: string // v2 contact shape
    nonGrove: string // v1 contact shape
  }
}

const DEFAULT_EXTERNAL_ID_FIELD = 'external_id_value'

const PARTNER_CONFIG: Record<PartnerId, PartnerConfig> = {
  maxassist: {
    partnerKey: 'maxassist',
    logo: mangoLogo,
    popupLogo: maxAssistIcon,
    theme: maxAssistTheme,
    externalIdFieldByGrove: {
      grove: 'external_id_value',
      nonGrove: 'guarantor_external_id',
    },
  },
  dentrix: {
    partnerKey: 'dentrix',
    logo: dentrixLogo,
    popupLogo: dentrixLogoWhite,
    theme: dentrixTheme,
    desktopAppName: 'Dentrix Phones',
    desktopAppInstallers: {
      mac: 'https://electron-dentrix-phones.s3.us-west-2.amazonaws.com/Dentrix+Phones.dmg',
      windows:
        'https://electron-dentrix-phones.s3.us-west-2.amazonaws.com/Dentrix+Phones+Setup.exe',
    },
  },
  'dentrix-ascend': {
    partnerKey: 'dentrix-ascend',
    logo: dentrixAscendLogo,
    popupLogo: dentrixAscendLogoWhite,
    theme: dentrixTheme,
    desktopAppName: 'Dentrix Ascend Phones',
    desktopAppInstallers: {
      mac: 'https://electron-dentrix-ascend-phones.s3.us-west-2.amazonaws.com/Dentrix+Ascend+Phones.dmg',
      windows:
        'https://electron-dentrix-ascend-phones.s3.us-west-2.amazonaws.com/Dentrix+Ascend+Phones+Setup.exe',
    },
  },
  dentally: {
    partnerKey: 'dentally',
    logo: dentallyLogo,
    popupLogo: dentallyLogoWhite,
    theme: dentallyTheme,
  },
  denticon: {
    partnerKey: 'denticon',
    logo: denticonLogo,
    popupLogo: denticonLogoWhite,
    theme: denticonTheme,
  },
  '': {
    logo: mangoLogo,
    popupLogo: mangoLogoWhite,
    theme: defaultTheme,
  },
} as const

/**
 * The installed desktop build's partner (window.electron.getPartnerId), never
 * the PBX's. Null while the Electron lookup is pending; '' for the base build,
 * the web app, an unknown id, or a failed lookup.
 */
export const useInstalledAppPartner = (): PartnerId | null => {
  const canLookup = canLookupInstalledAppPartner()
  const [partnerId, setPartnerId] = React.useState<PartnerId | null>(
    canLookup ? null : '',
  )

  React.useEffect(() => {
    if (!canLookup) return
    let cancelled = false
    void fetchInstalledAppPartner().then((id) => {
      if (!cancelled) setPartnerId(id)
    })
    return () => {
      cancelled = true
    }
  }, [canLookup])

  return partnerId
}

/**
 * Hook to get the current partner based on the current route or environment
 * @returns The current partner identifier
 */
export const useCurrentPartner = (): PartnerId => {
  const { pbx } = useUserState()
  // Electron partner (already validated) wins over the PBX key.
  return resolvePartnerId(useInstalledAppPartner(), pbx?.partner?.key)
}

/**
 * Partners whose customers have texting hidden by default until it is enabled
 * on the company in Mango Admin.
 */
const HIDDEN_TEXTING_FALLBACK_PARTNERS: PartnerId[] = [
  Partners.Dentrix,
  Partners['Dentrix-Ascend'],
]

/**
 * Hook to determine whether the texting experience should be shown.
 *
 * The value is configured per customer (company) in Mango Admin and served via
 * `pbx.features.show_texting`. When the API does not provide the field yet
 * (older backend deployments), fall back to the legacy partner behavior:
 * hidden for Dentrix and Dentrix Ascend, shown for everyone else.
 * @returns Boolean indicating if texting should be shown
 */
export const useShowTexting = (): boolean => {
  const { pbx } = useUserState()
  const currentPartner = useCurrentPartner()

  const showTexting = pbx?.features?.show_texting
  if (typeof showTexting === 'boolean') {
    return showTexting
  }

  return !HIDDEN_TEXTING_FALLBACK_PARTNERS.includes(currentPartner)
}

/**
 * Hook to check if we are using a partner
 * @returns Boolean indicating if we are using a partner
 */
export const useIsPartner = (): boolean => {
  const currentPartner = useCurrentPartner()
  return currentPartner !== ''
}

/**
 * Hook to get the current partner's configuration
 * @returns The partner configuration object
 */
export const usePartnerConfig = (): PartnerConfig => {
  const currentPartner = useCurrentPartner()
  const { pbx } = useUserState()

  const baseConfig = PARTNER_CONFIG[currentPartner] ?? PARTNER_CONFIG['']

  // Merge with pbx.partner data if available
  if (pbx?.partner?.deep_link_pattern) {
    return {
      ...baseConfig,
      deepLinkPattern: pbx.partner.deep_link_pattern,
    }
  }

  return baseConfig
}

/**
 * Resolves the external ID field for the current partner based on grove mode.
 */
export const getPartnerExternalIdField = (
  partnerConfig: PartnerConfig,
  isGrove: boolean,
): string => {
  if (partnerConfig.externalIdFieldByGrove) {
    return isGrove
      ? partnerConfig.externalIdFieldByGrove.grove
      : partnerConfig.externalIdFieldByGrove.nonGrove
  }

  // All other partners default to the standard v2 external ID field.
  return DEFAULT_EXTERNAL_ID_FIELD
}

/**
 * Hook to resolve the partner external ID field once from company grove state.
 */
export const usePartnerExternalIdField = (): string => {
  const partnerConfig = usePartnerConfig()
  const { pbx } = useUserState()
  const isGrove = pbx?.features?.is_grove ?? false

  return getPartnerExternalIdField(partnerConfig, isGrove)
}

/**
 * Hook to get the current partner's logo
 * @returns The logo URL for the current partner, or the default Mango logo
 */
export const usePartnerLogo = (): string => {
  const partnerConfig = usePartnerConfig()
  return partnerConfig.logo || mangoLogo
}

/**
 * Hook to get the current partner's popup logo
 * @returns The logo URL for the current partner, or the default Mango logo
 */
export const usePartnerPopupLogo = (): string => {
  const partnerConfig = usePartnerConfig()
  return partnerConfig.popupLogo || mangoLogoWhite
}

/**
 * Hook to get the current partner's desktop product name (e.g. "Dentrix Ascend
 * Phones"), for branding update/download copy. Defaults to "Mango Voice".
 */
export const usePartnerDesktopAppName = (): string => {
  const partnerConfig = usePartnerConfig()
  return partnerConfig.desktopAppName || 'Mango Voice'
}

/**
 * Hook to get where "Download" should send the user: the partner's branded
 * installer for this OS when one exists, otherwise the Mango Voice download page.
 */
export const usePartnerDesktopAppDownloadUrl = (): string => {
  const { desktopAppInstallers } = usePartnerConfig()
  const platform = getDesktopAppPlatform()
  return (
    (desktopAppInstallers && platform && desktopAppInstallers[platform]) ||
    urls.DESKTOP_APP_DOWNLOAD
  )
}
